import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet
} from '@mui/joy';
import SmsIcon from '@mui/icons-material/Sms';
import axiosConfig from '../util/axiosConfig';

const tableSheetSx = {
    width: '100%',
    maxWidth: '100%',
    borderRadius: 'sm',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    WebkitOverflowScrolling: 'touch',
    border: '1px solid',
    borderColor: 'divider'
};

const smsHistoryTableSx = {
    width: 'max-content',
    minWidth: '100%',
    tableLayout: 'fixed',
    '& tbody tr:nth-of-type(even)': {
        backgroundColor: '#f9f9f9'
    },
    '& tbody tr:hover': {
        backgroundColor: '#fff5ed'
    },
    '& thead': {
        position: 'sticky',
        top: 0,
        zIndex: 10
    },
    '& thead th': {
        backgroundColor: '#eb7a24',
        color: 'white',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderBottom: '2px solid #e55125',
        whiteSpace: 'nowrap'
    },
    '& td, & th': {
        padding: '12px',
        border: '1px solid',
        borderColor: 'divider',
        verticalAlign: 'top',
        overflow: 'hidden'
    },
    '& th:nth-of-type(1), & td:nth-of-type(1)': {
        width: 120,
        minWidth: 120,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(2), & td:nth-of-type(2)': {
        width: 100,
        minWidth: 100,
        whiteSpace: 'nowrap'
    },
    '& th:nth-of-type(3), & td:nth-of-type(3)': {
        width: 320,
        minWidth: 320,
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'break-word'
    }
};

const messageCellTextSx = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
};

const SMSHistory = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [smsData, setSmsData] = useState({
        smsHistory: [],
        dateRange: { from: '', to: '' }
    });
    const [page, setPage] = useState(1);
    const pageSize = 50;

    useEffect(() => {
        fetchSMSHistory();
    }, []);

    const fetchSMSHistory = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/sms-history');
            setSmsData(response.data);
        } catch (err) {
            console.error('Error fetching SMS history:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load SMS history data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading SMS history...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    const { smsHistory = [] } = smsData;
    const hasSMS = smsHistory.length > 0;
    const totalPages = Math.max(1, Math.ceil((smsHistory.length || 0) / pageSize));
    const currentPage = Math.min(page, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageData = smsHistory.slice(startIndex, endIndex);

    const PaginationControls = () => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 1 }}>
            <Typography level="body-sm" sx={{ color: '#6e7072' }}>
                Showing {hasSMS ? `${startIndex + 1}-${Math.min(endIndex, smsHistory.length)} of ${smsHistory.length}` : '0 of 0'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    style={{ padding: '6px 10px', cursor: currentPage <= 1 ? 'not-allowed' : 'pointer' }}
                >
                    Prev
                </button>
                <Typography level="body-sm" sx={{ color: '#404041', minWidth: 80, textAlign: 'center' }}>
                    Page {currentPage} / {totalPages}
                </Typography>
                <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    style={{ padding: '6px 10px', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }}
                >
                    Next
                </button>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ p: 0, m: 0, width: '100%', height: '100%', minWidth: 0 }}>
            <Card variant="outlined">
                <CardContent sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <SmsIcon sx={{ color: '#eb7a24', fontSize: '1.5rem' }} />
                        <Typography level="h3" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                            SMS History
                        </Typography>
                    </Box>

                    {/* Date range heading intentionally removed */}

                    {!hasSMS ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography level="body-lg" sx={{ color: '#6e7072' }}>
                                No SMS history available
                            </Typography>
                        </Box>
                    ) : (
                        <Sheet variant="outlined" sx={tableSheetSx}>
                            <PaginationControls />
                            <Table
                                aria-label="SMS history table"
                                sx={smsHistoryTableSx}
                            >
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Time</th>
                                        <th>SMS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentPageData.map((sms, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Typography level="body-md" sx={{ whiteSpace: 'nowrap' }}>
                                                    {sms.date || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-md" sx={{ whiteSpace: 'nowrap' }}>
                                                    {sms.time || '-'}
                                                </Typography>
                                            </td>
                                            <td>
                                                <Typography level="body-sm" sx={messageCellTextSx}>
                                                    {sms.message || '-'}
                                                </Typography>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <PaginationControls />
                        </Sheet>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default SMSHistory;

